import type { MaybeRefOrGetter } from "vue";
import { createSharedComposable, useNow } from "@vueuse/core";
import { differenceInCalendarDays, differenceInCalendarYears, differenceInMinutes, format, formatDistance, isValid, minutesToMilliseconds } from "date-fns";
import { computed, toValue } from "vue";
import { jsDateFromAspDateString } from "@/common/lib/date";

function convertToDate(value: string) {
  const date = new Date(value);
  return isValid(date) ? date : jsDateFromAspDateString(value);
}

function normalize(value: Date | string) {
  if (value instanceof Date) {
    return value;
  }
  return convertToDate(value);
}

const useNowShared = createSharedComposable(useNow);

export function useRelativeFormattedDate(date: MaybeRefOrGetter<Date>) {
  const now = useNowShared(({ interval: minutesToMilliseconds(1) }));
  const diffInMinutes = computed(() => differenceInMinutes(toValue(now), toValue(date)));
  const diffInCalendarDays = computed(() => differenceInCalendarDays(toValue(now), toValue(date)));
  const diffInCalendarYears = computed(() => differenceInCalendarYears(toValue(now), toValue(date)));
  const justNow = computed(() => diffInMinutes.value < 1);
  const lessThanHourAgo = computed(() => diffInMinutes.value < 60);
  const today = computed(() => diffInCalendarDays.value === 0);
  const yesterday = computed(() => diffInCalendarDays.value === 1);
  const thisYear = computed(() => diffInCalendarYears.value === 0);

  const relative = computed(() => {
    const value = toValue(date);
    if (justNow.value) return "just now";
    if (lessThanHourAgo.value) return `${diffInMinutes.value} ${diffInMinutes.value === 1 ? "minute" : "minutes"} ago`;
    if (today.value) return format(value, "p 'Today'");
    if (yesterday.value) return format(value, "p 'Yesterday'");
    if (thisYear.value) return format(value, "h:mm a MMM d");
    return format(value, "MMM d, y");
  });

  return relative;
}

export function useFormatDistanceDate(date: MaybeRefOrGetter<Date>) {
  const now = useNowShared(({ interval: minutesToMilliseconds(1) }));
  return computed(() => formatDistance(toValue(date), toValue(now), { addSuffix: true }));
}

export function useFormattedDate(value: MaybeRefOrGetter<Date | string>) {
  const date = computed(() => normalize(toValue(value)));

  const distance = useFormatDistanceDate(date);
  const short = computed(() => format(date.value, "PPP"));
  const shortTime = computed(() => format(date.value, "hh:mm a"));
  const full = computed(() => format(date.value, "MMMM do, yyyy, EEEE 'at' hh:mm a"));
  const long = computed(() => date.value.toLocaleString("en", { timeStyle: "medium", dateStyle: "short" }));
  const longDate = computed(() => format(date.value, "EEEE, MMMM do, yyyy"));

  return {
    date,
    distance,
    short,
    shortTime,
    long,
    full,
    longDate,
  };
}
